/**
 * Every string the two signup forms render (spec section 10).
 *
 * Labels are here rather than in the components because the owner edits copy
 * without touching layout (CLAUDE.md, "Content and configuration"), and
 * because a label is the accessible name of a control — changing one is a
 * copy decision with an accessibility consequence, and both belong in the same
 * place.
 *
 * Three things in here are load bearing:
 *
 * 1. **Every field has a visible label.** No placeholder-only labelling
 *    anywhere (spec section 18). Hints supplement labels; they never replace
 *    them.
 * 2. **The free-text service field is additive.** Its helper text says so in
 *    plain words, because a provider who reads it as "or type your own"
 *    destroys the category density reporting the site exists to produce
 *    (CLAUDE.md).
 * 3. **Error messages describe the rule, never echo the input.** The wording
 *    of a validation message comes from the shared Zod schemas; the strings
 *    here are the framing around them.
 */

export const formsContent = {
  common: {
    /** Rendered next to the label of every optional control. */
    optionalLabel: 'Optional',
    /** One line above the fields, so "required" is stated once rather than starred. */
    requiredNote: "Every field is required unless it's marked optional.",
    /**
     * Prefixes each inline error for assistive technology, so the meaning is
     * not carried by colour alone. Rendered visually hidden rather than as
     * visible punctuated text (`brand-voice.md`, punctuation).
     */
    errorPrefix: 'Error',
    errorSummaryTitle: "That didn't send yet.",
    errorSummaryIntro: 'Check the following and try again.',
    submittingLabel: 'Sending…',
    /** Transport failed. Retryable right now. */
    networkError:
      "That didn't reach us. It might be the connection. Check it and try the button again.",
    /** The server answered with a failure. Retryable in a moment. */
    serverError: 'Something went wrong on our end. Try the button again in a moment.',
    rateLimitedError:
      "That's more submissions than we expected from one connection. Wait a few minutes and try again.",
    /** The server rejected something the form does not render an input for. */
    unmappedFieldError: "Something in that submission wasn't accepted. Check the fields above.",
    /**
     * The hidden anti-spam field (spec section 19, CLAUDE.md "Honeypot from day
     * one"). Its container is `aria-hidden`, so this text exists for the rare
     * agent that renders the page without CSS rather than for a screen reader.
     *
     * Deliberately *not* "Website": the provider form has a real website field,
     * and two controls with the same visible label is a genuine ambiguity for
     * anyone — or anything — reading the page by its labels. The trap is the
     * field's `name`, which stays `website` because that is what a bot looks
     * for (shared/schemas/fields.ts).
     */
    honeypot: {
      label: 'Leave this field empty',
    },
    privacy: {
      prefix: 'We use what you enter here to contact you about Maryland early access. See the',
      linkLabel: 'privacy notice',
      linkTo: '/privacy',
      suffix: 'for the details.',
    },
  },

  owner: {
    /** Accessible description of the form as a whole. */
    description: 'Two fields, and a first name if you want to give it.',
    email: {
      label: 'Email address',
      hint: 'Where I send your early access update. Nothing else.',
    },
    zipCode: {
      label: 'ZIP code',
      hint: "Where your horse lives, so I know which part of the state you're in.",
    },
    firstName: {
      label: 'First name',
      hint: 'So I can use your name.',
    },
  },

  provider: {
    description: 'Tell me who you are, what you do, and where you work.',
    name: {
      label: 'Your name',
      hint: 'The person I should ask for.',
    },
    businessName: {
      label: 'Business name',
      hint: 'If you work under your own name, use that.',
    },
    email: {
      label: 'Email address',
      hint: 'Where I send your invitation to claim your profile.',
    },
    primaryCategory: {
      label: 'Primary service',
      hint: 'The main thing you do. Owners searching for it find you under it.',
      /** First option of the select. Not a label — the label is above the control. */
      placeholder: 'Choose a service',
      loadingLabel: 'Loading services…',
      errorMessage: "The service list didn't load.",
      retryLabel: 'Load the service list again',
    },
    additionalCategories: {
      legend: 'Anything else you do',
      hint: "Check everything else you offer. Your primary service is already counted, so it's not in this list.",
      emptyNote: 'Choose a primary service first and the rest of the list appears here.',
    },
    otherServices: {
      label: "A service that isn't on the list",
      /**
       * Additive, never a replacement. See the file header and CLAUDE.md.
       *
       * This line keeps its negative construction, which `brand-voice.md`
       * otherwise forbids. It is the sentence that stops a provider reading the
       * field as "or type your own", and free text replacing the structured
       * selection would destroy the category density reporting the site exists
       * to produce. A functional exception, deliberately taken.
       */
      hint: "This gets added to what you checked above. It doesn't replace it. Up to 200 characters.",
    },
    zipCode: {
      label: 'Business ZIP code',
      hint: "Where you're based. You can cover any area you like, in or out of Maryland.",
    },
    phone: {
      label: 'Phone',
      hint: "How I reach you if email isn't easiest.",
    },
    websiteUrl: {
      label: 'Website',
      hint: 'For example, myfarm.com.',
    },
    /**
     * Plenty of the people who take care of horses keep their own. Ticking this
     * writes a second lead for the same email on the owner side, which the
     * unique constraint allows because it is normalized email *and* audience
     * type (CLAUDE.md). It is a second list, not a second offer — the owner row
     * carries no founding eligibility.
     *
     * The label is the visitor speaking, which is why it is "I have horses".
     * The hint is the site speaking back, as every other hint in this module
     * does, so the two "I"s are never the same person and never adjacent.
     */
    alsoOwner: {
      label: 'I have horses of my own',
      hint: "I'll add you to the owner list too, so you hear when the network opens where you are.",
    },
    notes: {
      label: 'Anything else I should know',
      hint: 'Up to 1000 characters.',
    },
  },
} as const;
